/**
 * Exemplar Editor
 * Sheet/modal for creating and editing exemplars in the AI Sandbox.
 * Opens pre-filled from promote action, blank from "Add New", or pre-filled from "Edit".
 */

import { useState, useEffect } from 'react'
import { X, Save, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { useCreateExemplar, useUpdateExemplar } from '@/hooks'
import type {
  Exemplar,
  ExemplarCategory,
  ExemplarSourceType,
} from '@/lib/repositories'

const CATEGORIES: { value: ExemplarCategory; label: string }[] = [
  { value: 'general', label: 'General' },
  { value: 'signal_explanation', label: 'Signal Explanation' },
  { value: 'trading_philosophy', label: 'Trading Philosophy' },
  { value: 'regime_transition', label: 'Regime Transition' },
  { value: 'confirmation_logic', label: 'Confirmation Logic' },
  { value: 'ticker_specific', label: 'Ticker-Specific' },
  { value: 'discipline', label: 'Discipline' },
  { value: 'entries_exits', label: 'Entries & Exits' },
  { value: 'psychology', label: 'Psychology' },
  { value: 'risk_management', label: 'Risk Management' },
]

const SOURCE_LABELS: Record<ExemplarSourceType, string> = {
  raw_ai: 'Raw AI',
  founder_edited: 'Founder Edited',
  direct_authored: 'Direct Authored',
}

interface ExemplarEditorProps {
  open: boolean
  onClose: () => void
  /** When editing, pass the existing exemplar */
  exemplar?: Exemplar | null
  /** Pre-fill data when promoting from a conversation */
  prefill?: {
    question: string
    answer: string
    sourceType: ExemplarSourceType
    sourceConversationId?: string
    sourceMessageId?: string
  }
}

export function ExemplarEditor({
  open,
  onClose,
  exemplar,
  prefill,
}: ExemplarEditorProps) {
  const createMutation = useCreateExemplar()
  const updateMutation = useUpdateExemplar()

  const isEditing = !!exemplar
  const isPending = createMutation.isPending || updateMutation.isPending

  const [question, setQuestion] = useState('')
  const [answer, setAnswer] = useState('')
  const [category, setCategory] = useState<ExemplarCategory>('general')
  const [tagsInput, setTagsInput] = useState('')

  // Reset form when opening
  useEffect(() => {
    if (!open) return

    if (exemplar) {
      setQuestion(exemplar.question)
      setAnswer(exemplar.answer)
      setCategory(exemplar.category)
      setTagsInput(exemplar.tags.join(', '))
    } else if (prefill) {
      setQuestion(prefill.question)
      setAnswer(prefill.answer)
      setCategory('general')
      setTagsInput('')
    } else {
      setQuestion('')
      setAnswer('')
      setCategory('general')
      setTagsInput('')
    }
  }, [open, exemplar, prefill])

  const parseTags = (): string[] =>
    tagsInput
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean)

  const handleSave = async () => {
    if (!question.trim() || !answer.trim()) {
      toast.error('Question and answer are required')
      return
    }

    try {
      if (isEditing && exemplar) {
        await updateMutation.mutateAsync({
          id: exemplar.id,
          data: {
            question: question.trim(),
            answer: answer.trim(),
            category,
            tags: parseTags(),
          },
        })
        toast.success('Exemplar updated')
      } else {
        await createMutation.mutateAsync({
          question: question.trim(),
          answer: answer.trim(),
          category,
          tags: parseTags(),
          sourceType: prefill?.sourceType || 'direct_authored',
          sourceConversationId: prefill?.sourceConversationId,
          sourceMessageId: prefill?.sourceMessageId,
        })
        toast.success('Exemplar created — embedding will generate shortly')
      }
      onClose()
    } catch (error) {
      toast.error(
        `Failed to ${isEditing ? 'update' : 'create'} exemplar: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="h-[97vh]">
        <SheetHeader className="flex flex-row items-center justify-between">
          <SheetTitle className="text-white text-lg font-semibold">
            {isEditing ? 'Edit Exemplar' : 'New Exemplar'}
          </SheetTitle>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-neutral-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </Button>
        </SheetHeader>

        <div className="px-4 pb-6 flex-1 overflow-y-auto space-y-5">
          {/* Source info (read-only when promoted) */}
          {(prefill || exemplar?.sourceType) && (
            <div className="flex items-center gap-2 text-xs text-neutral-500">
              <span className="px-2 py-0.5 rounded bg-white/[0.04] border border-white/[0.06]">
                {SOURCE_LABELS[
                  prefill?.sourceType || exemplar?.sourceType || 'direct_authored'
                ]}
              </span>
              {(prefill?.sourceConversationId ||
                exemplar?.sourceConversationId) && (
                <span className="text-neutral-600">from conversation</span>
              )}
            </div>
          )}

          {/* Question */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-neutral-400">
              Question <span className="text-red-400">*</span>
            </label>
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="What question should trigger this exemplar?"
              rows={3}
              className={cn(
                'w-full bg-white/[0.04] border border-white/[0.06] rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-neutral-500 resize-none',
                'focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500/50'
              )}
            />
          </div>

          {/* Answer */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-neutral-400">
              Answer <span className="text-red-400">*</span>
            </label>
            <textarea
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="The ideal AI response for this question..."
              rows={8}
              className={cn(
                'w-full bg-white/[0.04] border border-white/[0.06] rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-neutral-500 resize-none',
                'focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500/50'
              )}
            />
          </div>

          {/* Category */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-neutral-400">
              Category
            </label>
            <select
              value={category}
              onChange={(e) =>
                setCategory(e.target.value as ExemplarCategory)
              }
              className={cn(
                'w-full bg-white/[0.04] border border-white/[0.06] rounded-lg px-3 py-2.5 text-sm text-white',
                'focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500/50',
                'appearance-none cursor-pointer'
              )}
            >
              {CATEGORIES.map((cat) => (
                <option
                  key={cat.value}
                  value={cat.value}
                  className="bg-neutral-900 text-white"
                >
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          {/* Tags */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-neutral-400">
              Tags
            </label>
            <Input
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="momentum, breakout, earnings (comma-separated)"
              className="bg-white/[0.04] border-white/[0.06] text-white placeholder:text-neutral-500 text-sm"
            />
            {parseTags().length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {parseTags().map((tag) => (
                  <span
                    key={tag}
                    className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/20"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Save Button */}
          <Button
            onClick={handleSave}
            disabled={isPending || !question.trim() || !answer.trim()}
            className="w-full bg-gradient-to-br from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-black font-semibold h-11 shadow-lg shadow-emerald-500/25 disabled:opacity-40"
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {isEditing ? 'Update Exemplar' : 'Create Exemplar'}
              </>
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
